import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  ArrowLeft,
  Calculator,
  Check,
  Loader2,
  Sparkles,
  ShieldCheck,
  MapPin,
  Hammer,
} from "lucide-react";
import { toast } from "sonner";
import PageHeader from "@/components/layout/PageHeader";
import { Progress } from "@/components/ui/progress";
import {
  ADU_QUESTIONS,
  SIZE_PRESETS,
  computeAduEstimate,
  formatUSD,
  isAnswerComplete,
} from "@/lib/aduCalculator";
import { submitAduLead } from "@/lib/api";
import { BUSINESS } from "@/lib/site";

const INITIAL_ANSWERS = {
  adu_type: "",
  size_sqft: "",
  finish_level: "",
  bathrooms: "",
  kitchen_type: "",
  foundation: "",
  utilities: [],
  site_condition: "",
};

const STAGES = {
  INTRO: "intro",
  STEPS: "steps",
  RESULT: "result",
  SUCCESS: "success",
};

export default function ADUCalculatorPage() {
  const [stage, setStage] = useState(STAGES.INTRO);
  const [stepIdx, setStepIdx] = useState(0);
  const [answers, setAnswers] = useState(INITIAL_ANSWERS);
  const [customSize, setCustomSize] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [serverResponse, setServerResponse] = useState(null);
  const [lead, setLead] = useState({
    full_name: "",
    phone: "",
    email: "",
    property_address: "",
    project_notes: "",
    preferred_time: "",
  });

  const totalSteps = ADU_QUESTIONS.length;
  const currentQ = ADU_QUESTIONS[stepIdx];
  const progress = stage === STAGES.STEPS ? ((stepIdx + 1) / (totalSteps + 1)) * 100
    : stage === STAGES.RESULT ? 100 : 0;

  const estimate = useMemo(() => computeAduEstimate(answers), [answers]);
  const canAdvance = currentQ ? isAnswerComplete(currentQ, answers) : false;

  const startCalc = () => {
    setStage(STAGES.STEPS);
    setStepIdx(0);
  };

  const selectSingle = (key, val) => setAnswers((a) => ({ ...a, [key]: val }));
  const toggleMulti = (key, val) =>
    setAnswers((a) => {
      const set = new Set(a[key] || []);
      set.has(val) ? set.delete(val) : set.add(val);
      return { ...a, [key]: Array.from(set) };
    });

  const handleSizePick = (preset) => {
    setAnswers((a) => ({ ...a, size_sqft: preset }));
    setCustomSize("");
  };
  const handleCustomSize = (e) => {
    const val = e.target.value.replace(/[^\d]/g, "");
    setCustomSize(val);
    setAnswers((a) => ({ ...a, size_sqft: val ? Number(val) : "" }));
  };

  const goNext = () => {
    if (!canAdvance) return;
    if (stepIdx < totalSteps - 1) setStepIdx((i) => i + 1);
    else setStage(STAGES.RESULT);
  };
  const goPrev = () => {
    if (stage === STAGES.RESULT) { setStage(STAGES.STEPS); return; }
    if (stepIdx === 0) { setStage(STAGES.INTRO); return; }
    setStepIdx((i) => i - 1);
  };

  const handleLeadChange = (k) => (e) =>
    setLead({ ...lead, [k]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!lead.full_name || !lead.phone || !lead.email) {
      toast.error("Please share your name, phone and email.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...lead,
        adu_type: answers.adu_type || "Not Sure",
        size_sqft: Number(answers.size_sqft) || 500,
        finish_level: answers.finish_level || "Standard",
        bathrooms: answers.bathrooms || "1 Bathroom",
        kitchen_type: answers.kitchen_type || "Full Kitchen",
        foundation: answers.foundation || "Not sure",
        utilities: answers.utilities || [],
        site_condition: answers.site_condition || "Not sure",
      };
      const resp = await submitAduLead(payload);
      setServerResponse(resp);
      setStage(STAGES.SUCCESS);
      toast.success("Thanks — we'll be in touch shortly.");
    } catch (err) {
      const msg =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Submission failed. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  const resetAll = () => {
    setStage(STAGES.INTRO);
    setStepIdx(0);
    setAnswers(INITIAL_ANSWERS);
    setCustomSize("");
    setLead({ full_name: "", phone: "", email: "", property_address: "", project_notes: "", preferred_time: "" });
    setServerResponse(null);
  };

  return (
    <div data-testid="adu-calculator-page">
      <PageHeader
        overline="ADU Cost Calculator"
        title="Estimate your ADU budget range in seconds."
        sub="Planning a garage conversion, detached ADU, or backyard unit? Use our free ADU Cost Calculator to get a realistic budget range before scheduling a full consultation with ME Builders."
        image="https://images.unsplash.com/photo-1711426793020-9eb590f2fc12"
        breadcrumbs={[{ label: "Home", to: "/" }, { label: "ADU Calculator" }]}
      />

      {/* Intro / front-screen */}
      {stage === STAGES.INTRO && (
        <section className="section-pad" data-testid="adu-intro">
          <div className="container-page grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-sm bg-brand-limestone border border-brand-line text-xs font-semibold tracking-[0.18em] uppercase text-brand-ink">
                <MapPin className="w-3.5 h-3.5 text-brand-olive" /> Available in Los Angeles County
              </div>
              <h2 className="heading-2 mt-6">Build your ADU with ME Builders.</h2>
              <p className="body-base mt-5 max-w-xl">
                Thinking about a garage conversion, detached ADU, or backyard unit? Use our free ADU Cost
                Calculator to get a realistic budget range in seconds. Every property is different, but this
                tool gives you a helpful starting point before scheduling a full consultation.
              </p>

              <div className="mt-8 flex flex-wrap items-center gap-3">
                <button
                  data-testid="adu-start-cta"
                  onClick={startCalc}
                  className="btn-primary"
                >
                  <Calculator className="w-4 h-4" /> Calculate My ADU Cost
                </button>
                <Link to="/services/adu" className="btn-secondary">
                  Learn About ADUs
                </Link>
              </div>

              <ul className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-xl">
                {[
                  "Takes ~60 seconds",
                  "Tailored to LA County",
                  "Free — no signup required",
                  "Real ranges, no fake quotes",
                ].map((t, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm text-brand-ink">
                    <Check className="w-4 h-4 text-brand-olive" strokeWidth={2.5} /> {t}
                  </li>
                ))}
              </ul>
            </div>

            <div className="lg:col-span-5">
              <div className="card-base p-8 relative overflow-hidden" data-testid="adu-typical-range-card">
                <div className="absolute -top-12 -right-12 w-48 h-48 rounded-full bg-amber-100/40 blur-2xl" />
                <p className="overline">Typical ADU budget range</p>
                <div className="mt-4 flex items-baseline gap-2">
                  <Sparkles className="w-5 h-5 text-amber-500" strokeWidth={1.75} />
                  <p className="font-heading text-4xl md:text-5xl font-extrabold tracking-tighter text-brand-ink">
                    $150,000
                  </p>
                </div>
                <p className="font-heading text-2xl font-bold text-brand-ash mt-1">
                  – $450,000<span className="text-amber-600">+</span>
                </p>
                <p className="body-sm mt-5">
                  Most LA-area ADUs fall in this range depending on type, size, finishes, utilities, and site
                  conditions. Use the calculator below for a tailored range.
                </p>
                <div className="mt-6 flex flex-wrap gap-2">
                  {["Garage Conv.", "Detached", "Attached", "Junior ADU", "Backyard"].map((t) => (
                    <span key={t} className="badge-trust !bg-white !border !border-brand-line">{t}</span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Step-by-step questions */}
      {stage === STAGES.STEPS && (
        <section className="section-pad" data-testid="adu-steps">
          <div className="container-page max-w-3xl">
            <div className="flex items-center justify-between mb-3 text-xs uppercase tracking-[0.2em] font-semibold text-brand-ash">
              <span>Step {stepIdx + 1} of {totalSteps}</span>
              <span>{Math.round(progress)}% complete</span>
            </div>
            <Progress value={progress} className="h-1.5 bg-brand-limestone" data-testid="adu-progress" />

            <div className="mt-10">
              <h2 className="heading-2" data-testid="adu-step-title">{currentQ.title}</h2>
              {currentQ.helper && <p className="body-sm mt-3">{currentQ.helper}</p>}

              <div className="mt-8">
                {currentQ.type === "single" && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="adu-single-options">
                    {currentQ.options.map((opt) => {
                      const active = answers[currentQ.key] === opt;
                      return (
                        <button
                          key={opt}
                          data-testid={`adu-opt-${opt.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                          onClick={() => selectSingle(currentQ.key, opt)}
                          className={`group text-left px-5 py-5 rounded-md border transition-all ${
                            active
                              ? "bg-brand-obsidian text-white border-brand-obsidian shadow-[0_8px_24px_rgba(26,26,24,0.18)]"
                              : "bg-white border-brand-line text-brand-ink hover:border-brand-obsidian"
                          }`}
                        >
                          <div className="flex items-center justify-between gap-3">
                            <span className="font-heading font-bold text-base md:text-lg">{opt}</span>
                            <span className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 ${active ? "border-amber-400 bg-amber-400" : "border-brand-line"}`}>
                              {active && <Check className="w-3 h-3 text-brand-obsidian" strokeWidth={3} />}
                            </span>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}

                {currentQ.type === "multi" && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="adu-multi-options">
                    {currentQ.options.map((opt) => {
                      const active = (answers[currentQ.key] || []).includes(opt);
                      return (
                        <button
                          key={opt}
                          data-testid={`adu-multi-${opt.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                          onClick={() => toggleMulti(currentQ.key, opt)}
                          className={`text-left px-5 py-4 rounded-md border transition-all flex items-center gap-3 ${
                            active
                              ? "bg-brand-obsidian text-white border-brand-obsidian"
                              : "bg-white border-brand-line text-brand-ink hover:border-brand-obsidian"
                          }`}
                        >
                          <span className={`w-5 h-5 rounded-sm border-2 flex items-center justify-center shrink-0 ${active ? "border-amber-400 bg-amber-400" : "border-brand-line"}`}>
                            {active && <Check className="w-3 h-3 text-brand-obsidian" strokeWidth={3} />}
                          </span>
                          <span className="font-semibold">{opt}</span>
                        </button>
                      );
                    })}
                  </div>
                )}

                {currentQ.type === "size" && (
                  <div data-testid="adu-size-options">
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      {SIZE_PRESETS.map((s) => {
                        const active = Number(answers.size_sqft) === s && !customSize;
                        return (
                          <button
                            key={s}
                            data-testid={`adu-size-${s}`}
                            onClick={() => handleSizePick(s)}
                            className={`px-4 py-5 rounded-md border text-center transition ${
                              active
                                ? "bg-brand-obsidian text-white border-brand-obsidian"
                                : "bg-white border-brand-line text-brand-ink hover:border-brand-obsidian"
                            }`}
                          >
                            <p className="font-heading text-xl font-extrabold tracking-tighter">{s.toLocaleString()}</p>
                            <p className="text-xs uppercase tracking-[0.18em] mt-1 opacity-75">sq ft</p>
                          </button>
                        );
                      })}
                    </div>
                    <div className="mt-5">
                      <label className="label-base">Or enter a custom size</label>
                      <div className="flex items-center gap-3">
                        <input
                          data-testid="adu-size-custom"
                          inputMode="numeric"
                          placeholder="e.g. 850"
                          className="input-base max-w-[180px]"
                          value={customSize}
                          onChange={handleCustomSize}
                        />
                        <span className="text-sm text-brand-ash">sq ft</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="mt-12 flex items-center justify-between border-t border-brand-line pt-6">
                <button
                  data-testid="adu-step-back"
                  onClick={goPrev}
                  className="btn-ghost"
                >
                  <ArrowLeft className="w-4 h-4" /> Back
                </button>
                <button
                  data-testid="adu-step-next"
                  onClick={goNext}
                  disabled={!canAdvance}
                  className={`btn-primary ${!canAdvance ? "opacity-40 cursor-not-allowed" : ""}`}
                >
                  {stepIdx === totalSteps - 1 ? "See My Estimate" : "Continue"}
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Result */}
      {stage === STAGES.RESULT && (
        <ResultStage
          estimate={estimate}
          answers={answers}
          lead={lead}
          submitting={submitting}
          onLeadChange={handleLeadChange}
          onSubmit={handleSubmit}
          onBack={goPrev}
          onReset={resetAll}
        />
      )}

      {/* Success */}
      {stage === STAGES.SUCCESS && (
        <section className="section-pad" data-testid="adu-success">
          <div className="container-page max-w-2xl text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-brand-obsidian mb-6">
              <Check className="w-8 h-8 text-amber-400" strokeWidth={2.5} />
            </div>
            <h2 className="heading-2">Thank you.</h2>
            <p className="body-base mt-4">
              {serverResponse?.message ||
                "ME Builders will review your ADU project details and contact you shortly."}
            </p>
            {serverResponse?.estimate && (
              <div className="mt-8 card-base p-7 text-left">
                <p className="overline">Your saved budget range</p>
                <p className="font-heading text-3xl md:text-4xl font-extrabold tracking-tighter text-brand-ink mt-2">
                  {formatUSD(serverResponse.estimate.expected_low)} – {formatUSD(serverResponse.estimate.expected_high)}
                </p>
                <p className="body-sm mt-2">A copy was emailed to {lead.email}.</p>
              </div>
            )}
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <a href={BUSINESS.phoneHref} className="btn-primary">
                Call {BUSINESS.phone}
              </a>
              <button onClick={resetAll} className="btn-secondary" data-testid="adu-restart">
                Run Another Estimate
              </button>
            </div>
          </div>
        </section>
      )}

      {stage !== STAGES.SUCCESS && (
        <section className="section-pad bg-brand-limestone">
          <div className="container-page max-w-4xl">
            <p className="overline mb-3">Why our estimates are useful</p>
            <h3 className="heading-2 mb-6">Real ranges, built on real LA jobs.</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {[
                { icon: ShieldCheck, title: "No fake quotes", text: "We show ranges — not made-up flat numbers. Final pricing happens after a site walk." },
                { icon: MapPin, title: "Calibrated to LA", text: "Numbers reflect Los Angeles County permit fees, site conditions and labor rates." },
                { icon: Hammer, title: "Built by builders", text: "Pricing logic comes from real ADU projects ME Builders has delivered, not a generic template." },
              ].map((t, i) => {
                const Icon = t.icon;
                return (
                  <div key={i} className="card-base p-6">
                    <Icon className="w-5 h-5 text-brand-olive" strokeWidth={1.75} />
                    <p className="font-heading text-lg font-bold mt-4">{t.title}</p>
                    <p className="body-sm mt-2">{t.text}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}

// ============== Result stage (extracted for clarity) ==============
function ResultStage({ estimate, answers, lead, submitting, onLeadChange, onSubmit, onBack, onReset }) {
  return (
    <section className="section-pad" data-testid="adu-result">
      <div className="container-page max-w-5xl">
        <button onClick={onBack} className="btn-ghost mb-6" data-testid="adu-result-back">
          <ArrowLeft className="w-4 h-4" /> Edit answers
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7">
            <p className="overline">Your estimated ADU budget range</p>
            <h2 className="heading-2 mt-3">Here's where your project likely lands.</h2>

            <div className="mt-8 space-y-4">
              <ResultRow
                label="Reasonable estimate"
                value={formatUSD(estimate.reasonable_estimate)}
                accent="ghost"
                testid="adu-result-reasonable"
              />
              <ResultRow
                label="Expected project range"
                value={`${formatUSD(estimate.expected_low)} – ${formatUSD(estimate.expected_high)}`}
                accent="primary"
                testid="adu-result-expected"
              />
              <ResultRow
                label="High-end / complex project"
                value={`${formatUSD(estimate.high_end_estimate)}+`}
                accent="ghost"
                testid="adu-result-highend"
              />
            </div>

            <p className="body-sm mt-8 leading-relaxed">
              This estimate is based on the project details you selected. Every property is different, and the
              final price may change depending on plans, permits, utilities, access, engineering, city requirements,
              and finish selections.
            </p>

            <details className="mt-6 card-base p-5">
              <summary className="cursor-pointer font-heading font-bold text-brand-ink">
                See how this was calculated
              </summary>
              <ul className="mt-4 text-sm text-brand-ash space-y-1.5" data-testid="adu-breakdown">
                <li className="flex justify-between"><span>Base ({answers.adu_type || '—'} · {answers.size_sqft || 0} sq ft)</span><span>{formatUSD(estimate.base_cost)}</span></li>
                <li className="flex justify-between"><span>Finish adjustment ({answers.finish_level || '—'})</span><span>{formatUSD(estimate.finish_adjustment)}</span></li>
                <li className="flex justify-between"><span>Bathrooms ({answers.bathrooms || '—'})</span><span>{formatUSD(estimate.bathrooms_cost)}</span></li>
                <li className="flex justify-between"><span>Kitchen ({answers.kitchen_type || '—'})</span><span>{formatUSD(estimate.kitchen_cost)}</span></li>
                <li className="flex justify-between"><span>Foundation ({answers.foundation || '—'})</span><span>{formatUSD(estimate.foundation_cost)}</span></li>
                <li className="flex justify-between"><span>Utilities ({(answers.utilities || []).length} selected)</span><span>{formatUSD(estimate.utilities_cost)}</span></li>
                <li className="flex justify-between"><span>Site ({answers.site_condition || '—'})</span><span>{formatUSD(estimate.site_cost)}</span></li>
                <li className="flex justify-between"><span>Plans & permits (flat)</span><span>{formatUSD(estimate.permits_cost)}</span></li>
                <li className="flex justify-between border-t border-brand-line pt-2 mt-2 font-bold text-brand-ink">
                  <span>Calculated total</span><span>{formatUSD(estimate.calculated_total)}</span>
                </li>
              </ul>
            </details>

            <div className="mt-6 px-5 py-4 rounded-sm bg-brand-limestone border border-brand-line text-xs text-brand-ash leading-relaxed" data-testid="adu-disclaimer">
              <strong className="text-brand-ink">Disclaimer:</strong> This calculator provides a preliminary
              ADU budget range only. Final pricing may vary based on approved plans, city requirements, utility
              conditions, site access, engineering, finish selections, and inspection corrections.
            </div>

            <button onClick={onReset} className="btn-ghost mt-6" data-testid="adu-reset">
              <ArrowLeft className="w-4 h-4" /> Start over
            </button>
          </div>

          <div className="lg:col-span-5">
            <div className="card-base p-7 sticky top-24">
              <p className="overline">Get a more accurate quote</p>
              <h3 className="heading-3 mt-2">Want a More Accurate ADU Quote?</h3>
              <p className="body-sm mt-3">
                ME Builders can review your property, ADU size, utility access, city requirements, and finish
                selections to prepare a more accurate project proposal.
              </p>

              <form onSubmit={onSubmit} className="mt-6 space-y-3" data-testid="adu-lead-form">
                <input
                  required
                  data-testid="adu-lead-name"
                  className="input-base"
                  placeholder="Full name"
                  value={lead.full_name}
                  onChange={onLeadChange("full_name")}
                />
                <input
                  required
                  data-testid="adu-lead-phone"
                  className="input-base"
                  placeholder="Phone number"
                  value={lead.phone}
                  onChange={onLeadChange("phone")}
                />
                <input
                  required
                  type="email"
                  data-testid="adu-lead-email"
                  className="input-base"
                  placeholder="Email"
                  value={lead.email}
                  onChange={onLeadChange("email")}
                />
                <input
                  data-testid="adu-lead-address"
                  className="input-base"
                  placeholder="Property address"
                  value={lead.property_address}
                  onChange={onLeadChange("property_address")}
                />
                <input
                  data-testid="adu-lead-time"
                  className="input-base"
                  placeholder="Preferred appointment time (e.g., weekdays after 5pm)"
                  value={lead.preferred_time}
                  onChange={onLeadChange("preferred_time")}
                />
                <textarea
                  rows={3}
                  data-testid="adu-lead-notes"
                  className="input-base resize-none"
                  placeholder="Project notes (optional)"
                  value={lead.project_notes}
                  onChange={onLeadChange("project_notes")}
                />
                <button
                  type="submit"
                  data-testid="adu-lead-submit"
                  disabled={submitting}
                  className="btn-primary w-full"
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
                  Request Free ADU Consultation
                </button>
                <p className="text-[11px] text-brand-ash">
                  By submitting you agree to be contacted by ME Builders. We respect your privacy.
                </p>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function ResultRow({ label, value, accent, testid }) {
  const isPrimary = accent === "primary";
  return (
    <div
      data-testid={testid}
      className={`flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 px-6 py-5 rounded-md border ${
        isPrimary
          ? "bg-brand-obsidian text-white border-brand-obsidian"
          : "bg-white border-brand-line"
      }`}
    >
      <p className={`text-xs uppercase tracking-[0.2em] font-semibold ${isPrimary ? "text-amber-400" : "text-brand-ash"}`}>
        {label}
      </p>
      <p className={`font-heading text-2xl md:text-3xl font-extrabold tracking-tighter ${isPrimary ? "text-white" : "text-brand-ink"}`}>
        {value}
      </p>
    </div>
  );
}
